/* PPD gallery lightbox: variant-aware zoom dialog.
 *
 * Owns the enlarged gallery view for blocks/_ppd-product-gallery.liquid.
 * Touch pan/pinch/double-tap (1-5x) are REUSED from
 * assets/drag-zoom-wrapper.js via the <drag-zoom-wrapper> element — this
 * file never reimplements those gestures. It only drives the same wrapper
 * through its public zoom API for buttons/keyboard, with a CSS-transform
 * fallback when the wrapper is unavailable (e.g. video media).
 *
 * Keeps existing behaviour: focus return, prev/next, close on backdrop and
 * Escape, gallery-autoplay pause on open. Adds: zoom in/out/reset buttons,
 * keyboard +/-/0, variant + live-price caption (selection sync lives in
 * assets/ppd-product.js), focus trap, reduced-motion and theme_motion
 * respect (transitions are owned by CSS via --theme-motion-duration).
 */
(function () {
  var MIN_ZOOM = 1;
  var MAX_ZOOM = 5;
  var ZOOM_STEP = 0.5;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function themeMotionDisabled() {
    return document.body && document.body.getAttribute('data-theme-motion') === 'none';
  }

  function shouldAnimate() {
    return !prefersReducedMotion() && !themeMotionDisabled();
  }

  function focusableIn(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return !el.disabled && el.offsetParent !== null;
    });
  }

  function initLightbox(root) {
    var lightbox = root.querySelector('[data-pdp-lightbox]');
    var opener = root.querySelector('[data-pdp-lightbox-open]');
    if (!lightbox || !opener) return;

    var stage = lightbox.querySelector('[data-pdp-lightbox-stage]');
    var figures = Array.prototype.slice.call(opener.querySelectorAll('.ppd-product-media'));
    var closeButton = lightbox.querySelector('[data-pdp-lightbox-close]');
    var zoomInButton = lightbox.querySelector('[data-pdp-lightbox-zoom-in]');
    var zoomOutButton = lightbox.querySelector('[data-pdp-lightbox-zoom-out]');
    var zoomResetButton = lightbox.querySelector('[data-pdp-lightbox-zoom-reset]');
    var toolbar = lightbox.querySelector('.pdp-lightbox__toolbar');
    var lastFocused = null;
    var current = 0;
    var fallbackScale = MIN_ZOOM;

    function wrapper() {
      return stage ? stage.querySelector('drag-zoom-wrapper') : null;
    }

    function stageImage() {
      var w = wrapper();
      if (w) return w.querySelector('img');
      return stage ? stage.querySelector('img') : null;
    }

    function applyFallbackScale() {
      var img = stageImage();
      if (!img) return;
      fallbackScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, fallbackScale));
      img.style.transform = fallbackScale === MIN_ZOOM ? '' : 'scale(' + fallbackScale + ')';
      /* Transitions live in CSS via --theme-motion-duration (0ms when
         settings.theme_motion is none) and are removed entirely under
         prefers-reduced-motion. JS stays instant either way. */
      if (!shouldAnimate()) {
        img.style.transition = 'none';
      } else {
        img.style.transition = '';
      }
      syncZoomButtons();
    }

    function syncZoomButtons() {
      var w = wrapper();
      var scale = w && typeof w.getZoom === 'function' ? w.getZoom() : fallbackScale;
      var isImage = Boolean(stageImage());
      [zoomInButton, zoomOutButton, zoomResetButton].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = !isImage;
      });
      if (toolbar) toolbar.hidden = !isImage;
      if (zoomInButton) zoomInButton.disabled = !isImage || scale >= MAX_ZOOM - 0.001;
      if (zoomOutButton) zoomOutButton.disabled = !isImage || scale <= MIN_ZOOM + 0.001;
    }

    function zoomIn() {
      var w = wrapper();
      if (w && typeof w.zoomIn === 'function') {
        w.zoomIn(ZOOM_STEP);
        syncZoomButtons();
        return;
      }
      fallbackScale = Math.min(MAX_ZOOM, fallbackScale + ZOOM_STEP);
      applyFallbackScale();
    }

    function zoomOut() {
      var w = wrapper();
      if (w && typeof w.zoomOut === 'function') {
        w.zoomOut(ZOOM_STEP);
        syncZoomButtons();
        return;
      }
      fallbackScale = Math.max(MIN_ZOOM, fallbackScale - ZOOM_STEP);
      applyFallbackScale();
    }

    function zoomReset() {
      var w = wrapper();
      if (w && typeof w.resetZoomToFit === 'function') {
        w.resetZoomToFit();
        syncZoomButtons();
        return;
      }
      fallbackScale = MIN_ZOOM;
      applyFallbackScale();
    }

    function show(index) {
      if (!figures.length || !stage) return;
      current = ((index % figures.length) + figures.length) % figures.length;
      var source = figures[current].querySelector('img, video, model-viewer, iframe');
      stage.replaceChildren();
      fallbackScale = MIN_ZOOM;
      if (!source) {
        syncZoomButtons();
        return;
      }
      var clone = source.cloneNode(true);
      clone.removeAttribute('width');
      clone.removeAttribute('height');
      clone.removeAttribute('loading');
      if (clone.tagName === 'VIDEO') clone.controls = true;

      if (clone.tagName === 'IMG') {
        /* REUSE: touch gestures delegated to drag-zoom-wrapper. */
        if (typeof customElements !== 'undefined' && customElements.get('drag-zoom-wrapper')) {
          var zoom = document.createElement('drag-zoom-wrapper');
          zoom.setAttribute('class', 'pdp-lightbox__zoom-wrapper');
          clone.setAttribute('ref', 'image');
          clone.removeAttribute('srcset');
          zoom.appendChild(clone);
          stage.appendChild(zoom);
          if (typeof zoom.resetZoomToFit === 'function') zoom.resetZoomToFit();
          else if (typeof zoom.setZoom === 'function') zoom.setZoom(MIN_ZOOM);
        } else {
          stage.appendChild(clone);
        }
      } else {
        stage.appendChild(clone);
      }
      syncZoomButtons();
    }

    function open(index) {
      lastFocused = document.activeElement;
      show(index);
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      opener.dispatchEvent(new CustomEvent('ppd:gallery-pause'));
      if (closeButton) closeButton.focus();
    }

    function close() {
      lightbox.hidden = true;
      document.body.style.overflow = '';
      if (stage) stage.replaceChildren();
      fallbackScale = MIN_ZOOM;
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    opener.addEventListener('click', function (event) {
      /* Let controls inside the gallery keep their own behaviour. */
      if (event.target.closest('button, a')) return;
      var figure = event.target.closest('.ppd-product-media');
      open(figure ? figures.indexOf(figure) : 0);
    });

    lightbox.addEventListener('click', function (event) {
      if (event.target === lightbox) close();
    });

    if (closeButton) closeButton.addEventListener('click', close);

    lightbox.querySelectorAll('[data-pdp-lightbox-step]').forEach(function (button) {
      button.addEventListener('click', function () {
        show(current + Number(button.getAttribute('data-pdp-lightbox-step')));
      });
    });

    if (zoomInButton) zoomInButton.addEventListener('click', zoomIn);
    if (zoomOutButton) zoomOutButton.addEventListener('click', zoomOut);
    if (zoomResetButton) zoomResetButton.addEventListener('click', zoomReset);

    /* Focus trap: keep Tab inside the dialog while open. Return on close
       preserves the existing return behaviour. */
    lightbox.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var items = focusableIn(lightbox);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'ArrowLeft') {
        show(current - 1);
      } else if (event.key === 'ArrowRight') {
        show(current + 1);
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomIn();
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomOut();
      } else if (event.key === '0') {
        event.preventDefault();
        zoomReset();
      }
    });
  }

  function init() {
    document.querySelectorAll('.ppd-product').forEach(initLightbox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
